import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { startOfDay } from "date-fns";
import { Suspense, useCallback, useEffect } from "react";
import { useFragment, useQueryLoader } from "react-relay";

import { ErrorDisplayingFallback } from "@/features/ErrorHandling/components/ErrorDisplayingFallback/ErrorDisplayingFallback";
import { Loader } from "@whoosh/design-system/src/components/display";
import {
  convertDateObjToServerStringFormat,
  convertDateTimeObjToServerStringFormat,
} from "@whoosh/utilities/src/dates";

import { PlatformSpecificErrorBoundary } from "../../features/ErrorHandling/components/PlatformSpecificErrorBoundary";
import { HomeTabsNavigatorParamList } from "../../navigators/HomeTabsNavigator/types";
import { HomeScreen_session$key } from "./__generated__/HomeScreen_session.graphql";
import { AnonymousMemberRedirect } from "./components/AnonymousMemberRedirect/AnonymousMemberRedirect";
import {
  HomeInternal,
  HomeInternalQueryString,
} from "./components/HomeInternal";
import { HomeInternalQuery } from "./components/__generated__/HomeInternalQuery.graphql";

type Props = {
  sessionRef: HomeScreen_session$key;
};

export const HomeScreen = ({ sessionRef }: Props) => {
  const session = useFragment(HomeScreenSessionFragmentString, sessionRef);
  const memberId = session?.currentClientProfile?.member?.id ?? null;

  const [queryRef, loadQuery] = useQueryLoader<HomeInternalQuery>(
    HomeInternalQueryString
  );

  const navigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const handleLoadQuery = useCallback(() => {
    if (!memberId) return;
    const today = new Date();
    const dateGTE = convertDateObjToServerStringFormat(today);
    const dateTimeGTE = convertDateTimeObjToServerStringFormat(
      startOfDay(today)
    );

    loadQuery(
      { dateGTE, dateTimeGTE, memberId },
      { fetchPolicy: "store-and-network" }
    );
  }, [memberId, loadQuery]);

  useEffect(() => {
    const unsubscribe = navigation.addListener("focus", handleLoadQuery);
    return unsubscribe;
  }, [navigation, handleLoadQuery]);

  useEffect(() => {
    handleLoadQuery();
  }, [handleLoadQuery]);

  /**
   * If we do not have a member ID, then we are an anonymous customer.
   *
   * In these cases, we redirect the user over to the agenda.
   */
  return !memberId ? (
    <AnonymousMemberRedirect />
  ) : (
    <PlatformSpecificErrorBoundary
      fallback={(eventId, error, resetError) => (
        <ErrorDisplayingFallback
          error={error}
          eventId={eventId}
          resetError={resetError}
        />
      )}
      children={
        <Suspense fallback={<Loader />}>
          {queryRef ? <HomeInternal queryRef={queryRef} /> : null}
        </Suspense>
      }
    />
  );
};

const HomeScreenSessionFragmentString = graphql`
  fragment HomeScreen_session on Session {
    currentClientProfile {
      member {
        ... on Member {
          id
        }
        ... on Customer {
          id
        }
      }
    }
  }
`;
